"use client";

import { useEffect } from "react";
import { useAtcAccordion } from "@/components/meds/atc/atc-list/atc-accordion/atc-accordion-context";
import { MouseIcon } from "lucide-react";

interface AtcKeyboardShortcutsHandlers {
  onToggleCompactView: () => void;
}

export const useAtcKeyboardShortcuts = ({
  onToggleCompactView,
}: AtcKeyboardShortcutsHandlers) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      )
        return;
      if (e.key === "s" || e.key === "S") {
        onToggleCompactView();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onToggleCompactView]);
};

export const useAtcKeyboardExpandAll = (items: AtcItem[]) => {
  const { expandedIds, expandAll, collapseAll } = useAtcAccordion();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      )
        return;
      if (e.key === "a" || e.key === "A") {
        if (expandedIds.size === 0) {
          expandAll(items);
        } else {
          collapseAll();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [expandedIds.size, expandAll, collapseAll, items]);
};

export const AtcKeyboardShortcutsHint = () => {
  return (
    <>
      <span className="flex h-6 items-center gap-2">
        <kbd className="inline-flex items-center justify-center rounded border bg-gray-100 px-1.5 py-0.5 font-mono font-medium text-gray-700">
          S
        </kbd>
        <span>Widok uproszczony / szczegółowy</span>
      </span>
      <span className="flex h-6 items-center gap-2">
        <kbd className="inline-flex items-center justify-center rounded border bg-gray-100 px-1.5 py-0.5 font-mono font-medium text-gray-700">
          A
        </kbd>
        <span>Rozwiń / zwiń wszystkie</span>
      </span>
      <span className="flex h-6 items-center gap-2">
        <kbd className="inline-flex items-center justify-center rounded border bg-gray-100 px-1.5 py-0.5 font-mono font-medium text-gray-700">
          Shift
        </kbd>
        <span>+</span>
        <kbd className="inline-flex items-center justify-center rounded border bg-gray-100 px-1.5 py-0.5 font-mono font-medium text-gray-700">
          <MouseIcon size={16} />
        </kbd>
        <span>Rozwiń / zwiń wszystkie zagnieżdżone przy kliknięciu</span>
      </span>
      <span className="flex h-6 items-center gap-2">
        <kbd className="inline-flex items-center justify-center rounded border bg-gray-100 px-1.5 py-0.5 font-mono font-medium text-gray-700">
          Ctrl
        </kbd>
        <span>+</span>
        <kbd className="inline-flex items-center justify-center rounded border bg-gray-100 px-1.5 py-0.5 font-mono font-medium text-gray-700">
          <MouseIcon size={16} />
        </kbd>
        <span>Wybierz bez zwijania/rozwijania</span>
      </span>
    </>
  );
};
